<template>
  <div>
    <b-card-code>
      <b-card-title>Latest Activity</b-card-title>
      <div ref="scrollContainer" class="log-scrollable" @scroll="handleScroll">
        <b-card class="justify-content-center align-items-center">
          <app-timeline v-if="activityLog.length > 0">
            <app-timeline-item
              v-for="(log, index) in activityLog"
              :key="index"
              variant="primary"
            >
              <div
                class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0"
              >
                <span
                  style="cursor: pointer"
                  @click="openLeadDetails(log?.lead?.data?.id)"
                  v-html="formatLogTitle(log)"
                />
                <small class="text-muted">{{
                  formatDateTime(log?.created_at)
                }}</small>
              </div>
            </app-timeline-item>

            <div v-if="loading" class="text-center my-2">
              <b-spinner small label="Loading..." />
            </div>
          </app-timeline>
          <div v-else>
            <b>No Activity Found</b>
          </div>
        </b-card>
      </div>
    </b-card-code>
  </div>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BButtonGroup,
  BButtonToolbar,
  BCard,
  BCardBody,
  BCardHeader,
  BCardText,
  BCardTitle,
  BCol,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormCheckbox,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormSelect,
  BLink,
  BListGroup,
  BListGroupItem,
  BModal,
  BPagination,
  BRow,
  BSpinner,
  BTab,
  BTabs,
  VBTooltip,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import Ripple from "vue-ripple-directive";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapGetters } from "vuex";
import {
  MILESTONE_BOARD_CREATE,
  MILESTONE_BOARD_DELETE,
  MILESTONE_BOARD_EDIT,
  MILESTONE_BOARD_SEE_ALL,
  MILESTONE_BOARD_UPDATE_STATUS,
} from "@/helpers/permissionsConstant";
import LvColorPicker from "lightvue/color-picker";
import BCardActions from "@core/components/b-card-actions/BCardActions.vue";
import AppTimelineItem from "@core/components/app-timeline/AppTimelineItem.vue";
import AppTimeline from "@core/components/app-timeline/AppTimeline.vue";
import BCardCode from "@core/components/b-card-code";

export default {
  name: "DashboardActivityLog",
  components: {
    BTabs,
    BCardCode,
    BTab,
    AppTimeline,
    AppTimelineItem,
    LvColorPicker,
    BForm,
    BRow,
    BCol,
    BButton,
    BButtonGroup,
    BButtonToolbar,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BCardBody,
    BBadge,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    BLink,
    BListGroup,
    BListGroupItem,
    BFormDatepicker,
    BFormRadio,
    BFormCheckbox,
    BCardActions,
  },
  directives: {
    Ripple,
    "b-tooltip": VBTooltip,
  },

  props: {
    viewAs: {
      type: String,
      default: "employee",
    },
    selectedUserId: {
      // 👈 pass from parent
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      activityLog: [],

      currentPage: 1,
      perPage: 5,
      lastPage: false,
      loading: false,
    };
  },

  mounted() {
    this.loadActivityLog();
  },
  watch: {
    selectedUserId(newVal) {
      this.currentPage = 1;
      this.activityLog = [];
      this.lastPage = false;
      this.loadActivityLog();
    },
  },
  methods: {
    formatLogTitle(log) {
      return `<b>${log?.lead?.data?.title}</b> Status updated to <b>${log?.leadStatus?.data?.name}</b> by ${log?.created_by?.name}`;
    },

    openLeadDetails(id) {
      const routeData = this.$router.resolve({
        name: "admin-lead-details",
        params: { id },
      });
      window.open(routeData.href, "_blank");
    },

    handleScroll(event) {
      const scrollContainer = event.target;
      const bottomReached =
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - 10;

      if (bottomReached && !this.loading) {
        this.loadActivityLog(); // Load more when bottom is reached
      }
    },

    async loadActivityLog() {
      if (this.loading || this.lastPage) return;

      this.loading = true;
      try {
        const response = await this.$api.get(
          `api/lead-activity-log?include=lead,leadStatus&view=${this.viewAs}`,
          {
            params: {
              page: this.currentPage,
              per_page: this.perPage,
              user_id: this.selectedUserId, // 👈 This is passed
            },
          }
        );

        const { data, meta } = response.data;

        this.activityLog.push(...data);
        this.currentPage++;

        if (this.currentPage > meta.last_page) {
          this.lastPage = true;
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error loading activity log",
            icon: "AlertCircleIcon",
            variant: "danger",
            text: error?.response?.data?.message ?? error.message,
          },
        });
      } finally {
        this.loading = false;
      }
    },

    formatDateTime(value) {
      if (value) {
        return this.$moment(value).format("MMM Do YY, h:mm:ss a");
      }
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/pages/ui-feather.scss";

.table-custom-style {
  font-size: 13px !important;
  white-space: nowrap !important;
  min-height: 140px !important;
  tr,
  th,
  td {
    //vertical-align: middle !important;
    text-align: left !important;
  }
}
.table-cell-margin {
  margin: 8px 0px;
}
.second-layer-margin {
  margin-top: 15px;
}

.required-label label::after {
  content: " *";
  color: red;
}

.log-scrollable {
  min-height: 50vh;
  max-height: 50vh;
  overflow-y: scroll;
}
</style>
